import { Button, Grid } from '@material-ui/core'
import { useEffect } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { createAddress, updateAddress } from '../../actions/addressActions'
import statesList from '../../data/states'
import { useInput } from '../../hooks/useInput'
import AutoCompleteSelect from '../Form/AutoCompleteSelect'
import CheckboxInput from '../Form/CheckboxInput'
import NumberInput from '../Form/NumberInput'
import TextInput from '../Form/TextInput'
import Loader from '../Loader/Loader'

interface Props {
  showSkipButton?: boolean
  selectedAddress?: Address
  setShowForm: (val: boolean) => void
}

const AddEditAddress = ({
  showSkipButton,
  selectedAddress,
  setShowForm,
}: Props) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const {
    value: companyName,
    bind: bindCompanyName,
    setValue: setCompanyName,
    error: companyNameError,
    setError: setCompanyNameError,
  } = useInput('')
  const {
    value: streetAddress,
    bind: bindStreetAddress,
    setValue: setStreetAddress,
    setError: setStreetAddressError,
    error: streetAddressError,
  } = useInput('')
  const {
    value: aptSuiteOther,
    bind: bindAptSuiteOther,
    setValue: setAptSuiteOther,
  } = useInput('')
  const {
    value: zipCode,
    setValue: setZipCode,
    setError: setZipCodeError,
    error: zipCodeError,
  } = useInput('')
  const {
    value: city,
    bind: bindCity,
    setValue: setCity,
    setError: setCityError,
    error: cityError,
  } = useInput('')
  const {
    value: state,
    setValue: setState,
    setError: setStateError,
    error: stateError,
  } = useInput('')
  const { value: isDefaultShipping, setValue: setIsDefaultShipping } =
    useInput(true)
  const { value: isDefaultReturn, setValue: setIsDefaultReturn } =
    useInput(true)

  const { loading: updateLoading } = useSelector(
    (state: RootStateOrAny) => state.addressUpdate
  )
  const { loading: createLoading } = useSelector(
    (state: RootStateOrAny) => state.addressCreate
  )
  const { userInfo } = useSelector((state: RootStateOrAny) => state.userLogin)
  const { project } = useSelector(
    (state: RootStateOrAny) => state.projectDetails
  )

  useEffect(() => {
    if (selectedAddress) {
      const {
        companyName,
        streetAddress,
        aptSuiteOther,
        zipCode,
        city,
        state,
        isDefaultShipping,
        isDefaultReturn,
      } = selectedAddress

      if (Object.keys(selectedAddress).length !== 0) {
        setCompanyName(companyName)
        setStreetAddress(streetAddress)
        setAptSuiteOther(aptSuiteOther)
        setZipCode(zipCode)
        setCity(city)
        setState(state)
        setIsDefaultShipping(isDefaultShipping)
        setIsDefaultReturn(isDefaultReturn)
      }
    }
  }, [
    dispatch,
    selectedAddress,
    setCompanyName,
    setStreetAddress,
    setAptSuiteOther,
    setZipCode,
    setCity,
    setState,
    setIsDefaultShipping,
    setIsDefaultReturn,
    companyName,
  ])

  const formIsValid = () => {
    let ready = true

    if (companyName === '') {
      ready = false
      setCompanyNameError('Please enter a company name.')
    } else {
      setCompanyNameError('')
    }

    if (streetAddress === '') {
      ready = false
      setStreetAddressError('Please enter a street address.')
    } else {
      setStreetAddressError('')
    }

    if (zipCode === '') {
      ready = false
      setZipCodeError('Please enter a zip code.')
    } else {
      setZipCodeError('')
    }

    if (state === '') {
      ready = false
      setStateError('Please select a state.')
    } else {
      setStateError('')
    }

    if (city === '') {
      ready = false
      setCityError('Please enter a city.')
    } else {
      setCityError('')
    }

    return ready
  }

  const submitHandler = () => {
    if (formIsValid()) {
      let address = {
        _id: '',
        companyName,
        streetAddress,
        aptSuiteOther,
        zipCode,
        state,
        city,
        isDefaultShipping,
        isDefaultReturn,
        user: userInfo.isAdmin ? project.user : userInfo._id,
      }

      if (!selectedAddress || Object.keys(selectedAddress).length === 0) {
        dispatch(createAddress(address))
      } else {
        address._id = selectedAddress._id
        dispatch(updateAddress(address))
      }
    }
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextInput
            name="companyName"
            label="Company name"
            bindValue={bindCompanyName}
            error={companyNameError}
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput
            name="streetAddress"
            label="Street address"
            bindValue={bindStreetAddress}
            error={streetAddressError}
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput
            name="aptSuiteOther"
            label="Apt/Suite/Other"
            bindValue={bindAptSuiteOther}
          />
        </Grid>
        <Grid item xs={12}>
          <div className={zipCodeError ? 'error' : ''}>
            <label htmlFor="size">Zip code</label>
          </div>
          <NumberInput
            value={zipCode}
            setValue={setZipCode}
            error={zipCodeError}
            label={null}
          />
        </Grid>
        <Grid item xs={8}>
          <TextInput
            name="city"
            label="City"
            bindValue={bindCity}
            error={cityError}
          />
        </Grid>
        <Grid item xs={4}>
          <AutoCompleteSelect
            label="State"
            name="state"
            options={statesList}
            value={state as string}
            setValue={setState}
            error={stateError}
            minWidth={148}
          />
        </Grid>

        <Grid item xs={12} className="p-b-0">
          <CheckboxInput
            name="isDefaultShipping"
            value={isDefaultShipping as boolean}
            setValue={setIsDefaultShipping}
            label={
              <div className="fs14 m-t-10 m-b-0">
                Use as default shipping address
              </div>
            }
          />
        </Grid>
        <Grid item xs={12} className="p-t-0 p-b-0">
          <CheckboxInput
            name="isDefaultReturn"
            value={isDefaultReturn as boolean}
            setValue={setIsDefaultReturn}
            label={
              <div className="fs14 m-t-10">Use as default return address</div>
            }
          />
        </Grid>
      </Grid>

      <div className="flex space-between m-t-40">
        {showSkipButton ? (
          <Button
            color="primary"
            variant="outlined"
            onClick={() => history.push('/createaquote')}
          >
            Skip for now
          </Button>
        ) : (
          <Button
            color="primary"
            variant="outlined"
            onClick={() => setShowForm(false)}
            disabled={updateLoading || createLoading}
            className={updateLoading || createLoading ? 'loading' : ''}
          >
            Cancel
          </Button>
        )}

        <Button
          color="primary"
          variant="contained"
          onClick={submitHandler}
          disabled={updateLoading || createLoading}
          className={updateLoading || createLoading ? 'loading' : ''}
        >
          {updateLoading || createLoading ? <Loader /> : 'Save'}
        </Button>
      </div>
    </>
  )
}

export default AddEditAddress
